import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Flex, Box, Text, TagBadge, Button, Trash, Edit, Markdown, Divider, Card, CardContent, TextArea, useToast, Menu, MenuItem, MenuTrigger, MenuSeparator, ListCardRow, type TagColor } from '@chatsdet/ui';
import {
  Shield,
  Key,
  Lock,
  GithubLogo,
  XLogo,
  CaretDown,
  WarningCircle,
  PlugsConnected,
  Lightning,
  DotsThreeVertical,
  LinkBreak,
  Eraser,
  IdentificationCard,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { API_URL } from '../../config';
import type { AuthProvider } from './CredentialForm';

type TestLoginType = 'mcp';

type CredentialItem = {
  id: string;
  name: string;
  environmentId?: string | null;
  environmentName?: string | null;
  authProvider: AuthProvider;
  isDefault: boolean;
  lastUsedAt?: Date | string | null;
  lastLoginSucceededAt?: Date | string | null;
  storageStateS3Key?: string | null;
  sessionValidationUrl?: string | null;
  updatedAt: Date | string;
  roleContext?: string | null;
  isEphemeral?: boolean;
  executionId?: string | null;
  workflowId?: string | null;
  hasInbox?: boolean;
};

type Props = {
  credential: CredentialItem;
  propertyId: string;
  onRefresh?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onUnlink?: () => void;
  onTestLogin?: (type: TestLoginType) => void;
  onClearStorageState?: () => void;
  variant?: 'card' | 'row';
  /** Row variant only: slot before the icon tile (e.g. a drag handle). */
  leading?: React.ReactNode;
  /** Row variant only: stagger index for the entrance animation. */
  index?: number;
};

const providerIcons: Record<AuthProvider, React.ReactNode> = {
  basic: <Lock size={16} />,
  google: <Shield size={16} />,
  google_oauth: <Shield size={16} />,
  github: <GithubLogo size={16} />,
  microsoft: <Shield size={16} />,
  api_key: <Key size={16} />,
  api_login: <PlugsConnected size={16} />,
  api_basic: <Lock size={16} />,
  twitter: <XLogo size={16} />,
  custom: <Shield size={16} />,
  test_sign_in: <IdentificationCard size={16} />,
};

const providerLabels: Record<AuthProvider, string> = {
  basic: 'Basic Auth',
  google: 'Google Login',
  google_oauth: 'Google (token)',
  github: 'GitHub Login',
  microsoft: 'Microsoft Login',
  api_key: 'API Key',
  api_login: 'API Login',
  api_basic: 'API Basic Auth',
  twitter: 'X (Twitter) Login',
  custom: 'Custom',
  test_sign_in: 'Test sign-in',
};

const providerTagColors: Record<AuthProvider, TagColor> = {
  basic: 'gray',
  google: 'red',
  google_oauth: 'red',
  github: 'purple',
  microsoft: 'blue',
  api_key: 'orange',
  api_login: 'teal',
  api_basic: 'orange',
  twitter: 'indigo',
  custom: 'green',
  test_sign_in: 'blue',
};

export function CredentialListItem({
  credential,
  propertyId,
  onRefresh,
  onEdit,
  onDelete,
  onUnlink,
  onTestLogin,
  onClearStorageState,
  variant = 'card',
  leading,
  index = 0,
}: Props) {
  const isRow = variant === 'row';
  const { toast } = useToast();
  const [showContext, setShowContext] = React.useState(false);
  const [roleContextValue, setRoleContextValue] = React.useState(credential.roleContext ?? '');
  const [roleContextDraft, setRoleContextDraft] = React.useState(credential.roleContext ?? '');
  const [savingRoleContext, setSavingRoleContext] = React.useState(false);
  const [editingRoleContext, setEditingRoleContext] = React.useState(false);
  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'Never';
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  };

  const saveField = async (field: 'roleContext', value: string) => {
    const body: Record<string, any> = { [field]: value.trim() || null };
    const res = await fetch(
      `${API_URL}/org/properties/${propertyId}/credentials/${credential.id}`,
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      }
    );
    if (!res.ok) {
      throw new Error(await res.text());
    }
    onRefresh?.();
  };

  const hasGuidance = (roleContextValue?.trim().length ?? 0) > 0;
  const hasVerifiedLogin = Boolean(credential.lastLoginSucceededAt || credential.storageStateS3Key);

  React.useEffect(() => {
    setRoleContextValue(credential.roleContext ?? '');
    setRoleContextDraft(credential.roleContext ?? '');
    setEditingRoleContext(false);
  }, [credential.id, credential.roleContext]);

  const canClearStorageState = Boolean(onClearStorageState && credential.storageStateS3Key);

  const handleMenuAction = (key: React.Key) => {
    if (key === 'edit') onEdit?.();
    else if (key === 'unlink') onUnlink?.();
    else if (key === 'delete') onDelete?.();
    else if (key === 'test-login') onTestLogin?.('mcp');
    else if (key === 'clear-storage-state') onClearStorageState?.();
  };

  const hasMenuActions = Boolean(
    onEdit || onDelete || onUnlink || onTestLogin || canClearStorageState
  );

  const badges = (
    <>
      {credential.isDefault && <TagBadge name="Default" color="blue" size="sm" />}
      {hasVerifiedLogin && <TagBadge name="Login verified" color="green" size="sm" />}
      {credential.hasInbox && <TagBadge name="Email inbox" color="purple" size="sm" />}
      {credential.sessionValidationUrl && !isRow && (
        <TagBadge name="API validation" color="gray" size="sm" />
      )}
      {credential.environmentName && (
        <TagBadge name={credential.environmentName} color="gray" size="sm" hideDot />
      )}
      {credential.isEphemeral && (
        <TagBadge
          name="Ephemeral"
          color="orange"
          icon={<Lightning size={10} weight="fill" />}
          size="sm"
        />
      )}
    </>
  );

  const executionLink =
    credential.isEphemeral && credential.executionId ? (
      credential.workflowId ? (
        <Link
          to="/flows/$workflowId"
          params={{ workflowId: credential.workflowId }}
          search={{ runId: credential.executionId }}
          className="text-xs text-primary hover:underline"
        >
          Debug run {credential.executionId.slice(0, 8)}
        </Link>
      ) : (
        <Link
          to="/test/$executionId"
          params={{ executionId: credential.executionId }}
          className="text-xs text-primary hover:underline"
        >
          Execution {credential.executionId.slice(0, 8)}
        </Link>
      )
    ) : null;

  const rowMenu = hasMenuActions ? (
    <div onClick={(e) => e.stopPropagation()}>
      <MenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className="p-1.5 text-foreground/70 hover:text-foreground hover:bg-muted"
          aria-label={`Actions for ${credential.name}`}
        >
          <DotsThreeVertical size={16} weight="bold" />
        </Button>
        <Menu onAction={handleMenuAction}>
          {onTestLogin ? <MenuItem id="test-login">Test login</MenuItem> : null}
          {onEdit ? (
            <MenuItem id="edit">
              <Edit size={14} className="mr-2" />
              Edit
            </MenuItem>
          ) : null}
          {onUnlink ? (
            <MenuItem id="unlink">
              <LinkBreak size={14} className="mr-2" />
              Unlink from template
            </MenuItem>
          ) : null}
          {canClearStorageState ? (
            <MenuItem id="clear-storage-state">
              <Eraser size={14} className="mr-2" />
              Clear saved session
            </MenuItem>
          ) : null}
          {(onEdit || onUnlink || onTestLogin || canClearStorageState) && onDelete ? (
            <MenuSeparator />
          ) : null}
          {onDelete ? (
            <MenuItem id="delete" destructive>
              <Trash size={14} className="mr-2" />
              Delete
            </MenuItem>
          ) : null}
        </Menu>
      </MenuTrigger>
    </div>
  ) : null;

  const guidance = hasGuidance ? (
    <Card className="mt-3 border border-border/80 bg-muted/30">
      <CardContent className="p-3 space-y-2">
        <Flex align="center" justify="between" gap="sm">
          <Flex align="center" gap="sm">
            <Box className="p-2 rounded-md bg-primary/10 text-primary">
              <WarningCircle size={16} />
            </Box>
            <div className="leading-tight">
              <Text weight="medium" size="sm">
                App-specific guidance & guardrails
              </Text>
              <Text size="xs" className="text-muted-foreground">
                Not-a-bug notes for this credential
              </Text>
            </div>
          </Flex>
          <Button
            size="sm"
            variant="ghost"
            onPress={() => setShowContext((prev) => !prev)}
            iconRight={
              <CaretDown
                size={14}
                className={
                  showContext ? 'rotate-180 transition-transform' : 'transition-transform'
                }
              />
            }
          >
            {showContext ? 'Hide AI guidance' : 'Show AI guidance'}
          </Button>
        </Flex>
        <AnimatePresence initial={false}>
          {showContext && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <Divider className="my-3" />
              <Box className="space-y-3 pb-1 pt-1">
                {roleContextValue && (
                  <Box className="space-y-1">
                    <Flex align="center" justify="between" gap="sm">
                      <Text
                        size="xs"
                        weight="medium"
                        className="text-muted-foreground uppercase tracking-wide"
                      >
                        Role Context
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft={<Edit size={12} />}
                        onPress={() => setEditingRoleContext(true)}
                      >
                        Edit
                      </Button>
                    </Flex>
                    {editingRoleContext ? (
                      <Box className="space-y-2 rounded-md border border-border/60 bg-background/60 p-2">
                        <TextArea
                          value={roleContextDraft}
                          onChange={setRoleContextDraft}
                          isDisabled={savingRoleContext}
                        />
                        <Flex gap="sm">
                          <Button
                            size="sm"
                            onPress={async () => {
                              try {
                                setSavingRoleContext(true);
                                const next = roleContextDraft.trim();
                                await saveField('roleContext', next);
                                toast.success('Role context updated');
                                setRoleContextValue(next);
                                setEditingRoleContext(false);
                              } catch (error) {
                                toast.error(
                                  error instanceof Error ? error.message : 'Failed to save'
                                );
                              } finally {
                                setSavingRoleContext(false);
                              }
                            }}
                            isDisabled={savingRoleContext}
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onPress={() => {
                              setRoleContextDraft(roleContextValue ?? '');
                              setEditingRoleContext(false);
                            }}
                            isDisabled={savingRoleContext}
                          >
                            Cancel
                          </Button>
                        </Flex>
                      </Box>
                    ) : (
                      <Markdown className="prose prose-xs max-w-none text-foreground prose-p:text-foreground prose-li:text-foreground">
                        {roleContextValue}
                      </Markdown>
                    )}
                  </Box>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  ) : null;

  if (isRow) {
    return (
      <ListCardRow
        leading={leading}
        index={index}
        icon={providerIcons[credential.authProvider]}
        title={credential.name}
        badges={badges}
        meta={
          <>
            <Text size="xs" className="text-muted-foreground">
              {providerLabels[credential.authProvider]}
            </Text>
            <span aria-hidden>·</span>
            <Text size="xs" className="text-muted-foreground">
              Updated {formatDate(credential.updatedAt)}
            </Text>
            {credential.lastUsedAt && (
              <>
                <span aria-hidden>·</span>
                <Text size="xs" className="text-muted-foreground">
                  Last used {formatDate(credential.lastUsedAt)}
                </Text>
              </>
            )}
            {executionLink}
          </>
        }
        actions={rowMenu}
        footer={guidance}
      />
    );
  }

  return (
    <Box className="group p-3 border border-border bg-card rounded-lg hover:border-primary/50 transition-colors">
      <Flex align="center" justify="between" className="mb-2">
        <Flex align="center" gap="md" className="flex-1 min-w-0">
          <Box className="flex items-center justify-center w-8 h-8 rounded-md bg-muted shrink-0">
            {providerIcons[credential.authProvider]}
          </Box>
          <Box className="flex-1 min-w-0">
            <Flex align="center" gap="sm" className="mb-0.5">
              <Text weight="medium" className="truncate">
                {credential.name}
              </Text>
              {badges}
            </Flex>
            <Flex align="center" gap="sm" className="text-xs text-muted-foreground">
              <TagBadge
                name={providerLabels[credential.authProvider]}
                color={providerTagColors[credential.authProvider]}
                icon={providerIcons[credential.authProvider]}
                size="sm"
              />
              <Text size="xs" className="text-muted-foreground">
                Updated {formatDate(credential.updatedAt)}
              </Text>
              {credential.lastUsedAt && (
                <>
                  <span aria-hidden>·</span>
                  <Text size="xs" className="text-muted-foreground">
                    Last used {formatDate(credential.lastUsedAt)}
                  </Text>
                </>
              )}
              {executionLink}
            </Flex>
          </Box>
        </Flex>
        <Flex gap="sm">
          {onTestLogin && (
            <Button variant="ghost" size="sm" onPress={() => onTestLogin('mcp')}>
              Test Login
            </Button>
          )}
          {onEdit && (
            <Button variant="ghost" size="sm" onPress={onEdit} iconLeft={<Edit size={14} />}>
              Edit
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost"
              size="sm"
              onPress={onDelete}
              iconLeft={<Trash size={14} />}
              className="text-destructive hover:text-destructive"
            >
              Delete
            </Button>
          )}
        </Flex>
      </Flex>
      {guidance}
    </Box>
  );
}
